import type { BOGOConfig, Offer, PromoCode } from '../data/types';

export type OfferFormFields = Omit<
  Offer,
  'id' | 'createdAt' | 'updatedAt' | 'version' | 'redemptionCount'
>;

function newPromoId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `pc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Clone promo rows for a new offer (fresh ids, no usage). */
function clonePromoCodesForDuplicate(source: PromoCode[]): PromoCode[] {
  return (source ?? []).map((pc) => ({
    ...pc,
    id: newPromoId(),
    offerId: '',
    usageCount: 0,
    createdAt: new Date().toISOString(),
    status: 'active' as const,
  }));
}

/**
 * Maps an existing offer into form state for "duplicate → create new offer".
 * Deep-clones nested fields so the source list/detail object is not mutated.
 */
export function offerToDuplicateFormState(source: Offer): OfferFormFields {
  const clone = JSON.parse(JSON.stringify(source)) as Offer;
  // Clear the name on duplicate so the user must enter a fresh, unique name
  // (copying the source name would otherwise trip the duplicate-name validation).
  const name = '';

  const {
    id: _i,
    createdAt: _c,
    updatedAt: _u,
    version: _v,
    redemptionCount: _r,
    ...rest
  } = clone;

  let typeConfig = rest.typeConfig;
  if (typeConfig?.kind === 'bogo') {
    const b = typeConfig as BOGOConfig;
    typeConfig = {
      ...b,
      bogoConfigId: undefined,
      buyRowIds: b.buyItemIds.map(() => ''),
      getRowIds: b.getItemIds.map(() => ''),
    };
  }

  return {
    ...rest,
    name,
    status: 'draft',
    typeConfig,
    promoCodes: [],
    schedule: {
      ...rest.schedule,
      startDate: new Date().toISOString().split('T')[0],
      endDate: null,
      noEndDate: true,
    },
  };
}
